import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, SceneComponent } from './types';
// import { TextureLoader } from 'three';

// const WIDTH = 1;
// const HEIGHT = 1;
// const DEPTH = 1;
const DEFAULT_INPUTS = {
  url: '/images/custom-sweep.png',
  label: null,
  labelScale: 1,
  isDefault: false,
  colorDefault: 'white',
  colorHover: 'white',
  colorClick: 'white',
  opacityDefault: 0.5,
  opacityHover: 0.5,
  opacityClick: 0.5
};

export class CustomSweepComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private sweep: any = null;

  private currentOpacity = 0.2;

  private clicked = false;

  // private textureLoader: TextureLoader;

  public label: any;

  public labelSize = [0, 0];

  inputs: Inputs = {
    ...DEFAULT_INPUTS
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false
  };

  onInit() {
    const THREE = this.context.three;
    // this.textureLoader = new THREE.TextureLoader();
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.pivot.rotation.x = Math.PI / 2;
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.currentOpacity = this.inputs.opacityDefault;

    this.makeCustomSweep();
    if (this.inputs.label) this.initLabel();
  }

  onDestroy() {
    if (this.sweep) {
      disposeMesh(this.sweep);
      this.sweep = null;
    }
    if (this.label) {
      disposeMesh(this.label);
      this.label = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    this.onInit();
  }

  // used to fire it from outside event handler,
  // as internal handler doesn't work if outside handler is defined
  handleHover(event: any) {
    if (!this.clicked) {
      if (event.hover) {
        this.currentOpacity = this.inputs.opacityHover;
      } else {
        this.currentOpacity = this.inputs.opacityDefault;
      }
      this.sweep.material.opacity = this.currentOpacity;
    }
  }

  handleClick() {
    if (!this.clicked) {
      this.currentOpacity = this.inputs.opacityClick;
      this.clicked = true;
    } else {
      this.currentOpacity = this.inputs.opacityDefault;
      this.clicked = false;
    }
    this.sweep.material.opacity = this.currentOpacity;
    setTimeout(() => {
      this.clicked = false;
    }, 1000);
  }

  setSelection(color: string) {
    this.inputs.colorDefault = color || this.inputs.colorClick;
    this.inputs.opacityDefault = color ? this.inputs.opacityDefault : this.inputs.opacityClick;
  }

  resetToDefaultColor() {
    this.inputs.colorDefault = DEFAULT_INPUTS.colorClick;
    this.inputs.opacityClick = DEFAULT_INPUTS.opacityClick;
    if (!this.sweep?.material) return;
    (this.sweep.material as any).color.setStyle(this.inputs.colorDefault);
  }

  setVisible(visible: boolean) {
    if (!this.sweep?.material) return;
    (this.sweep.material as any).opacity = visible ? this.currentOpacity : 0;
    if (this.label) this.label.material.opacity = visible ? 1 : 0;
  }

  setCircleVisible(visible: boolean) {
    if (!this.sweep?.material) return;
    (this.sweep.material as any).opacity = visible ? this.currentOpacity : 0;
  }

  private async makeCustomSweep() {
    const THREE = this.context.three;

    if (this.sweep) {
      disposeMesh(this.sweep);
      this.sweep = null;
    }

    // const texture = await this.textureLoader.load(this.inputs.url);

    // const geometry = new THREE.PlaneGeometry(1, 1);
    const geometry = new THREE.RingGeometry(0.35, 0.5, 32);
    const material: any = new THREE.MeshBasicMaterial({
      // map: texture,
      color: this.inputs.isDefault ? 'white' : '#9bf32b',
      transparent: true,
      opacity: this.currentOpacity,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      depthWrite: true,
      depthTest: true
    });
    // material.color.setStyle('white');

    this.sweep = new THREE.Mesh(geometry, material);
    this.pivot.add(this.sweep);
  }

  protected initLabel() {
    this.label = this.makeLabel(this.inputs.label, {
      fontsize: 48,
      textColor: {
        r: 255,
        g: 255,
        b: 255,
        a: 1.0
      },
      borderColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0
      },
      backgroundColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0
      },
      borderThickness: 0
    });

    this.label.rotation.y = Math.PI;

    this.pivot.add(this.label);
  }

  protected makeLabel(message, parameters) {
    const THREE = this.context.three;
    if (parameters === undefined) parameters = {};

    const fontface = parameters.hasOwnProperty('fontface') ? parameters.fontface : 'Arial';
    const fontsize = parameters.hasOwnProperty('fontsize') ? parameters.fontsize : 36;
    const borderThickness = parameters.hasOwnProperty('borderThickness')
      ? parameters.borderThickness
      : 4;
    const borderColor = parameters.hasOwnProperty('borderColor')
      ? parameters.borderColor
      : {
          r: 0,
          g: 0,
          b: 0,
          a: 1.0
        };
    const backgroundColor = parameters.hasOwnProperty('backgroundColor')
      ? parameters.backgroundColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1
        };
    const textColor = parameters.hasOwnProperty('textColor')
      ? parameters.textColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0
        };

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.font = `Bold ${fontsize}px ${fontface}`;
    const metrics = context.measureText(message);
    const textWidth = metrics.width;

    context.fillStyle = `rgba(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b},${backgroundColor.a})`;
    context.strokeStyle = `rgba(${borderColor.r},${borderColor.g},${borderColor.b},${borderColor.a})`;

    context.lineWidth = borderThickness;
    const rectW = (textWidth + borderThickness) * 1.1;
    const rectH = fontsize * 1.4 + borderThickness;
    this.roundRect(context, borderThickness / 2, borderThickness / 2, rectW, rectH, 8);

    context.fillStyle = `rgba(${textColor.r}, ${textColor.g}, ${textColor.b}, 1.0)`;
    context.fillText(message, borderThickness, fontsize + borderThickness);

    // crop exactly the rectangle size
    const label = this.crop(canvas, 0, 0, rectW + borderThickness, rectH + borderThickness);
    if (!label) return;

    const texture = new THREE.Texture(label);
    texture.needsUpdate = true;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.encoding = THREE.sRGBEncoding;
    texture.repeat.set(1, 1);

    const planeWidth = label.width / 1000; // scaling down
    const planeHeight = label.height / 1000;

    // need for dynamic mute button positioning
    this.labelSize = [planeWidth, planeHeight];

    const plane = this.makePlane(planeWidth, planeHeight, texture);
    plane.scale.setScalar(this.inputs.labelScale);
    return plane;
  }

  private makePlane(width, height, texture) {
    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(width, height, 10, 10);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      transparent: true,
      depthTest: true,
      depthWrite: true
    });
    return new THREE.Mesh(geometry, material);
  }

  private roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // eslint-disable-next-line class-methods-use-this
  private crop(can, x, y, w, h) {
    // get your canvas and a context for it
    const ctx = can.getContext('2d');

    // get the image data you want to keep.
    const imageData = ctx.getImageData(x, y, w * 2, h);

    // create a new cavnas same as clipped size and a context
    const newCan = document.createElement('canvas');
    newCan.width = w - x;
    newCan.height = h - y;
    const newCtx = newCan.getContext('2d');

    if (!newCtx) return;
    // put the clipped image on the new canvas.
    newCtx.putImageData(imageData, 0, 0);

    return newCan;
  }
}

export const customSweepComponentType = 'treedis.customSweepComponent';
export const makeCustomSweepComponent = () => new CustomSweepComponent();
